class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    this.initHorizontalFacets();
    this.initVendorSearch();
  }
  initHorizontalFacets() {
    if (this.classList.contains('facets__horizontal')) {
      this.setupHorizontalFacets();
    }
    this.horizontalObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          if (this.classList.contains('facets__horizontal')) {
            this.setupHorizontalFacets();
          }
        }
      });
    });
    this.querySelector('#FacetsWrapperDesktop') && this.horizontalObserver.observe(this.querySelector('#FacetsWrapperDesktop'), {
      childList: true,
      subtree: false
    });
  }

  initVendorSearch() {
    // Initialize both desktop and mobile vendor search
    this.initVendorSearchInstance('VendorSearchInput', 'VendorSearchButton', 'VendorSearchDropdown', 'VendorSearchSuggestions', 'VendorSearchMessage');
    this.initVendorSearchInstance('VendorSearchInputMobile', 'VendorSearchButtonMobile', 'VendorSearchDropdownMobile', 'VendorSearchSuggestionsMobile', 'VendorSearchMessageMobile');
  }

  initVendorSearchInstance(inputId, buttonId, dropdownId, suggestionsId, messageId) {
    const vendorSearchInput = document.getElementById(inputId);
    if (!vendorSearchInput) return;
    
    // Always refresh vendor filter data (in case filters were re-rendered)
    this.vendorFilterData = this.getVendorFilterData();
    if (!this.vendorFilterData) return;
    
    // Check if already initialized
    if (vendorSearchInput.dataset.vendorSearchInitialized === 'true') {
      // Just refresh the vendor data, event listeners are already set up
      return;
    }
    
    // Mark as initialized
    vendorSearchInput.dataset.vendorSearchInitialized = 'true';

    const vendorSearchButton = document.getElementById(buttonId);
    const vendorSearchDropdown = document.getElementById(dropdownId);
    const vendorSearchSuggestions = document.getElementById(suggestionsId);
    const vendorSearchMessage = document.getElementById(messageId);

    // Debounce autocomplete suggestions
    const debouncedShowSuggestions = debounce((searchTerm) => {
      this.showVendorSuggestions(searchTerm, dropdownId, suggestionsId, messageId, inputId);
    }, 200);

    // Input event - show autocomplete suggestions
    vendorSearchInput.addEventListener('input', (event) => {
      const searchTerm = event.target.value.trim();
      if (searchTerm.length > 0) {
        debouncedShowSuggestions(searchTerm);
      } else {
        this.hideVendorDropdown(dropdownId, inputId, messageId);
      }
    });

    // Focus event - show suggestions if there's text
    vendorSearchInput.addEventListener('focus', () => {
      const searchTerm = vendorSearchInput.value.trim();
      if (searchTerm.length > 0) {
        this.showVendorSuggestions(searchTerm, dropdownId, suggestionsId, messageId, inputId);
      }
    });

    // Enter key - trigger search
    vendorSearchInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.performVendorSearch(vendorSearchInput.value.trim(), dropdownId, suggestionsId, messageId, inputId);
      } else if (event.key === 'Escape') {
        this.hideVendorDropdown(dropdownId, inputId, messageId);
      }
    });

    // Search button click
    if (vendorSearchButton) {
      vendorSearchButton.addEventListener('click', () => {
        this.performVendorSearch(vendorSearchInput.value.trim(), dropdownId, suggestionsId, messageId, inputId);
      });
    }

    // Click outside to close dropdown
    document.addEventListener('click', (event) => {
      const container = vendorSearchInput.closest('.vendor-search-container');
      if (container && !container.contains(event.target)) {
        this.hideVendorDropdown(dropdownId, inputId, messageId);
      }
    });
  }

  getVendorFilterData() {
    // Find vendor filter section
    let vendorFilterDetails = null;
    const allDetails = this.querySelectorAll('details[id*="Details-"]');
    
    for (let details of allDetails) {
      const summary = details.querySelector('summary');
      if (summary) {
        const summaryText = summary.textContent.trim().toLowerCase();
        if (summaryText.includes('vendor')) {
          vendorFilterDetails = details;
          break;
        }
      }
      
      const checkboxes = details.querySelectorAll('input[type="checkbox"]');
      for (let checkbox of checkboxes) {
        if (checkbox.name && (checkbox.name.includes('filter.v.option.vendor') || checkbox.name.toLowerCase().includes('vendor'))) {
          vendorFilterDetails = details;
          break;
        }
      }
      if (vendorFilterDetails) break;
    }
    
    if (!vendorFilterDetails) {
      const allCheckboxes = this.querySelectorAll('input[type="checkbox"]');
      for (let checkbox of allCheckboxes) {
        if (checkbox.name && (checkbox.name.includes('filter.v.option.vendor') || checkbox.name.toLowerCase().includes('vendor'))) {
          vendorFilterDetails = checkbox.closest('details');
          break;
        }
      }
    }
    
    if (!vendorFilterDetails) return null;

    // Collect all vendor data
    const vendorCheckboxes = vendorFilterDetails.querySelectorAll('input[type="checkbox"]:not([disabled])');
    const vendors = [];

    vendorCheckboxes.forEach(checkbox => {
      let vendorName = '';
      const label = checkbox.closest('label');
      
      if (label) {
        const labelText = label.cloneNode(true);
        const countElement = labelText.querySelector('.facet-checkbox__count');
        if (countElement) countElement.remove();
        vendorName = labelText.textContent.trim();
      }
      
      if (!vendorName && checkbox.value) {
        vendorName = checkbox.value.trim();
      }
      
      if (vendorName) {
        vendors.push({
          name: vendorName,
          checkbox: checkbox,
          isChecked: checkbox.checked
        });
      }
    });

    return {
      details: vendorFilterDetails,
      vendors: vendors
    };
  }

  showVendorSuggestions(searchTerm, dropdownId = 'VendorSearchDropdown', suggestionsId = 'VendorSearchSuggestions', messageId = 'VendorSearchMessage', inputId = 'VendorSearchInput') {
    // Refresh vendor data to ensure we have current checkbox references
    this.vendorFilterData = this.getVendorFilterData();
    if (!this.vendorFilterData) return;

    const searchTermLower = searchTerm.toLowerCase();
    const vendorSearchDropdown = document.getElementById(dropdownId);
    const vendorSearchSuggestions = document.getElementById(suggestionsId);
    const vendorSearchMessage = document.getElementById(messageId);

    if (!vendorSearchDropdown || !vendorSearchSuggestions) return;

    // Filter matching vendors
    const matches = this.vendorFilterData.vendors.filter(vendor => 
      vendor.name.toLowerCase().includes(searchTermLower)
    );

    // Clear previous suggestions
    vendorSearchSuggestions.innerHTML = '';
    vendorSearchMessage.style.display = 'none';

    // Create suggestion items for matching vendors
    matches.forEach(vendor => {
      const li = document.createElement('li');
      li.className = 'vendor-suggestion-item';
      li.setAttribute('role', 'option');
      li.textContent = vendor.name;
      
      // Check current checkbox state
      const isCurrentlyChecked = vendor.checkbox && vendor.checkbox.checked;
      if (isCurrentlyChecked) {
        li.classList.add('vendor-suggestion-selected');
        li.textContent += ' ✓';
      }

      // Use vendor name to find checkbox dynamically on click
      li.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.selectVendorByName(vendor.name, inputId, dropdownId, messageId);
      });

      vendorSearchSuggestions.appendChild(li);
    });

    // Always add "Create your own" option at the end
    const createYourOwnLi = document.createElement('li');
    createYourOwnLi.className = 'vendor-suggestion-item vendor-suggestion-create';
    createYourOwnLi.setAttribute('role', 'option');
    createYourOwnLi.innerHTML = `
    <span class="vendor-create-icon">
<svg fill="#000000" viewBox="0 0 1920 1920" xmlns="http://www.w3.org/2000/svg">
    <path d="M392.26 1042.5c137.747-57.67 292.85-15.269 425.873 116.217l4.394 4.833c116.656 146.425 149.5 279.119 97.873 394.237-128.85 287.138-740.692 328.77-810.005 332.504L0 1896.442l61.953-91.83c.989-1.539 105.013-158.728 105.013-427.192 0-141.811 92.6-279.558 225.294-334.92ZM1728.701 23.052c54.923-1.099 99.96 15.268 135.111 49.43 40.643 40.644 58.109 87.877 56.021 140.603C1908.85 474.52 1423.33 953.447 1053.15 1280.79c-24.276-64.81-63.711-136.21-125.335-213.102l-8.787-9.886c-80.078-80.187-169.163-135.11-262.423-161.473C955.276 558.002 1460.677 33.927 1728.701 23.052Z" fill-rule="evenodd"/>
</svg></span> Create Your Own | Go to Customizer ➤`
;
    
    createYourOwnLi.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      window.location.href = 'https://custom.thewavroom.com';
    });

    vendorSearchSuggestions.appendChild(createYourOwnLi);

    // Show dropdown (always show if there's text in input, even if no matches)
    if (searchTerm.trim().length > 0) {
    vendorSearchDropdown.style.display = 'block';
    const vendorSearchInput = document.getElementById(inputId);
    if (vendorSearchInput) {
      vendorSearchInput.setAttribute('aria-expanded', 'true');
    }
    } else {
      vendorSearchDropdown.style.display = 'none';
    }
  }

  hideVendorDropdown(dropdownId = 'VendorSearchDropdown', inputId = 'VendorSearchInput', messageId = 'VendorSearchMessage') {
    const vendorSearchDropdown = document.getElementById(dropdownId);
    const vendorSearchInput = document.getElementById(inputId);
    const vendorSearchMessage = document.getElementById(messageId);
    
    if (vendorSearchDropdown) {
      vendorSearchDropdown.style.display = 'none';
    }
    if (vendorSearchInput) {
      vendorSearchInput.setAttribute('aria-expanded', 'false');
    }
    if (vendorSearchMessage) {
      vendorSearchMessage.style.display = 'none';
    }
  }

  performVendorSearch(searchTerm, dropdownId = 'VendorSearchDropdown', suggestionsId = 'VendorSearchSuggestions', messageId = 'VendorSearchMessage', inputId = 'VendorSearchInput') {
    // Refresh vendor data to ensure we have current checkbox references
    this.vendorFilterData = this.getVendorFilterData();
    if (!this.vendorFilterData || !searchTerm) {
      this.hideVendorDropdown(dropdownId, inputId, messageId);
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    
    // Find exact match first
    let exactMatch = this.vendorFilterData.vendors.find(vendor => 
      vendor.name.toLowerCase() === searchTermLower
    );

    // If no exact match, find first partial match
    if (!exactMatch) {
      exactMatch = this.vendorFilterData.vendors.find(vendor => 
        vendor.name.toLowerCase().includes(searchTermLower)
      );
    }

    if (exactMatch) {
      this.selectVendorByName(exactMatch.name, inputId, dropdownId, messageId);
    } else {
      // Show "No vendor found" message
      const vendorSearchMessage = document.getElementById(messageId);
      if (vendorSearchMessage) {
        vendorSearchMessage.textContent = 'No vendor found';
        vendorSearchMessage.style.display = 'block';
        vendorSearchMessage.className = 'vendor-search-message vendor-search-message-error';
      }
      this.hideVendorDropdown(dropdownId, inputId, messageId);
    }
  }

  selectVendorByName(vendorName, inputId = 'VendorSearchInput', dropdownId = 'VendorSearchDropdown', messageId = 'VendorSearchMessage') {
    // Refresh vendor data to get current checkbox references
    this.vendorFilterData = this.getVendorFilterData();
    if (!this.vendorFilterData) return;

    // Find vendor by name (case-insensitive)
    const vendorNameLower = vendorName.toLowerCase();
    const vendor = this.vendorFilterData.vendors.find(v => 
      v.name.toLowerCase() === vendorNameLower
    );

    if (!vendor || !vendor.checkbox) {
      console.warn('Vendor checkbox not found for:', vendorName);
      return;
    }

    this.selectVendor(vendor.checkbox, vendor.name, inputId, dropdownId, messageId);
  }

  selectVendor(checkbox, vendorName, inputId = 'VendorSearchInput', dropdownId = 'VendorSearchDropdown', messageId = 'VendorSearchMessage') {
    if (!checkbox || !this.vendorFilterData) return;

    // Verify checkbox is still in DOM
    if (!checkbox.isConnected) {
      // Refresh vendor data and try again
      this.vendorFilterData = this.getVendorFilterData();
      const vendorNameLower = vendorName.toLowerCase();
      const vendor = this.vendorFilterData.vendors.find(v => 
        v.name.toLowerCase() === vendorNameLower
      );
      if (!vendor || !vendor.checkbox) return;
      checkbox = vendor.checkbox;
    }

    // Open vendor filter section if closed
    if (!this.vendorFilterData.details.hasAttribute('open')) {
      this.vendorFilterData.details.setAttribute('open', 'open');
    }

    // Single-select behavior: Uncheck all other vendor checkboxes first
    const allVendorCheckboxes = this.vendorFilterData.details.querySelectorAll('input[type="checkbox"]:not([disabled])');
    allVendorCheckboxes.forEach(otherCheckbox => {
      if (otherCheckbox !== checkbox && otherCheckbox.checked) {
        otherCheckbox.checked = false;
        // Trigger input event for unchecked checkboxes to update filters
        otherCheckbox.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });

    // Check the selected checkbox if not already checked
    if (!checkbox.checked) {
      checkbox.checked = true;
      // Trigger input event to activate existing checkbox-based filter logic
      checkbox.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      // If already checked, just trigger the event to ensure filter is applied
      checkbox.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Clear search input and hide dropdown for the specific instance
    const vendorSearchInput = document.getElementById(inputId);
    if (vendorSearchInput) {
      vendorSearchInput.value = '';
    }
    this.hideVendorDropdown(dropdownId, inputId, messageId);

    // Clear any error messages
    const vendorSearchMessage = document.getElementById(messageId);
    if (vendorSearchMessage) {
      vendorSearchMessage.style.display = 'none';
    }
    if (inputId === 'VendorSearchInputMobile') {
      const menuDrawer = checkbox.closest('menu-drawer');
      if (menuDrawer && typeof menuDrawer.closeMenuDrawer === 'function') {
        // Small delay to ensure filter is applied before closing
        setTimeout(() => {
          menuDrawer.closeMenuDrawer('custom');
        }, 300);
      }
    }
  }
  setupHorizontalFacets() {
    document.removeEventListener('click', this.closeTab);
    this.querySelectorAll('.facets__disclosure__horizontal summary').forEach(item => {
      item.removeEventListener('click', this.openTab);
    });
    document.addEventListener('click', this.closeTab.bind(this));
    this.querySelectorAll('.facets__disclosure__horizontal summary').forEach((item) => {
      item.addEventListener('click', this.openTab.bind(this));
    });
  }
  openTab(event){
    var current = event.target.closest('details');
    if(current.classList.contains('details__opened')){
      current.classList.remove('details__opened');
    }else{
      setTimeout(function(){current.classList.add('details__opened');}, 100)
    }
  }
  closeTab(event){
    if(!this.classList.contains('facets__horizontal')) return;
    var index = -1;
    var current = event.target.closest('details');
    if(current){
      index = current.getAttribute('data-index');
    }
    this.querySelectorAll('.facets__disclosure__horizontal[open]').forEach((item) => {
      if(item.getAttribute('data-index') != index){
        item.removeAttribute('open');
        item.classList.remove('details__opened');
        item.querySelector('summary').setAttribute('aria-expanded', false);
      }
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    placeCollectionLoader();
    document.getElementById('ProductGridContainer').classList.add('loading_now');
    document.querySelector('.collection_facets_loader').classList.add('loading_now');
    
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.removePreloader();
        // Refresh vendor search data after filters are rendered
        const facetForm = document.querySelector('facet-filters-form');
        if (facetForm) {
          // Refresh vendor data (checkboxes may have been replaced during re-render)
          facetForm.initVendorSearch();
        }
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);    
    FacetFiltersForm.removePreloader();
    // Re-initialize vendor search after filters are rendered
    const facetForm = document.querySelector('facet-filters-form');
    if (facetForm) {
      facetForm.initVendorSearch();
    }
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static removePreloader(){
    window.dispatchEvent(new CustomEvent('updateGrid'));
    document.querySelector('.collection_facets_loader').classList.remove('loading_now');
    document.getElementById('ProductGridContainer').classList.remove('loading_now');
  }
  
  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const countContainer = document.getElementById('ProductCount');
    if(!countContainer) return false;
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    container.innerHTML = count;
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    var removed = false;
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.closest('details').remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (elementToRender.parentElement) {
          var _parent_current = document.querySelector(`#${elementToRender.parentElement.id}`);
        }
        if (index > 0 && elementToRender.parentElement && _parent_current) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          var _index = elementToRender.parentElement.dataset.index;
          var _fl = document.querySelector(`#${elementToRender.parentElement.id}`);
          if(_fl){
            if(_fl.querySelector(`.js-filter`)){
              _fl.querySelector(`.js-filter`).before(elementToRender);
            }
          }
          else{
            if(elementToRender.parentElement.classList.contains('mobile-facets__details')){
              var parent = document.querySelector(`#FacetFiltersFormMobile .facet-drawer__content__scroll`);
            }
            else{
              var parent = document.querySelector(`#FacetsWrapperDesktop`);
            }
            parent.insertBefore(elementToRender.parentElement, parent.children[_index])
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile')&&document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);